<template>
  <div>
    <div>
      <CustomLoader v-if="isLoading" :width="70"></CustomLoader>
    </div>
    
    <b-card class="mainCardBG">
      <b-card-text>
        <!-- Header Navigation -->
        <b-navbar
          class="formCard"
          style="border-radius: 5px !important; padding-left: 0px"
          toggleable="lg"
          type="dark"
          variant="primary"
        >
          <b-navbar-nav>
            <b-button @click="goBack" size="sm" variant="light">
              <md-icon icon="gear-fill" aria-hidden="true">skip_previous</md-icon>Back
            </b-button>
          </b-navbar-nav>
          <b-navbar-brand center style="margin-left: 0.5rem !important;">
            <md-icon icon="gear-fill" aria-hidden="true">person</md-icon>Driver Profile Details
          </b-navbar-brand>
          <b-navbar-nav class="ml-auto">
            <b-button @click="editDriver" size="sm" variant="outline-light" class="me-2">
              <md-icon icon="gear-fill" aria-hidden="true">edit</md-icon>Edit
            </b-button>
            <b-button @click="printDriverInfo" size="sm" variant="outline-light">
              <md-icon icon="gear-fill" aria-hidden="true">print</md-icon>Print
            </b-button>
          </b-navbar-nav>
        </b-navbar>

        <!-- Error Message -->
        <b-alert v-if="errorMessage" variant="danger" show dismissible>
          {{ errorMessage }}
        </b-alert>

        <!-- Driver Profile Header -->
        <b-card v-if="driver" class="formCard mt-3">
          <b-card-text>
            <div class="row align-items-center">
              <div class="col-md-3 text-center">
                <div class="driver-avatar-container">
                  <img 
                    :src="driver.photo || '/assets/default-avatar.png'" 
                    alt="Driver Photo"
                    class="driver-avatar"
                  >
                  <div class="status-badge" :class="getStatusClass(driver.isDriverActive)">
                    {{ getStatusText(driver.isDriverActive) }}
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <h3 class="driver-name">{{ driver.driverName }}</h3>
                <p class="driver-id text-muted">Driver ID: {{ driver.driverId }}</p>
                <div class="driver-badges">
                  <b-badge 
                    :variant="driver.driverType === '1' ? 'success' : 'warning'"
                    class="me-2"
                  >
                    {{ driver.driverType === '1' ? 'Permanent' : 'Temporary' }}
                  </b-badge>
                  <b-badge variant="info" class="me-2">
                    {{ getLicenseClass() }}
                  </b-badge>
                  <b-badge variant="secondary">
                    {{ driver.driverLocation || 'N/A' }}
                  </b-badge>
                </div>
              </div>
              <div class="col-md-3">
                <div class="quick-stats">
                  <div class="stat-item">
                    <md-icon icon="gear-fill" aria-hidden="true" class="text-primary">schedule</md-icon>
                    <span>{{ getExperienceYears() }} Years</span>
                  </div>
                  <div class="stat-item">
                    <md-icon icon="gear-fill" aria-hidden="true" class="text-success">local_shipping</md-icon>
                    <span>{{ driver.driverVehicleNumber || 'Not Assigned' }}</span>
                  </div>
                  <div class="stat-item">
                    <md-icon icon="gear-fill" aria-hidden="true" class="text-info">phone</md-icon>
                    <span>{{ driver.driverContact || 'N/A' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </b-card-text>
        </b-card>

        <!-- Main Content with Advanced Tabs -->
        <b-card v-if="driver" class="formCard mt-3">
          <b-card-text>
            <b-tabs content-class="mt-3" fill>
              <!-- Personal Information Tab -->
              <b-tab title="Personal Information" active>
                <div class="row">
                  <div class="col-md-6">
                    <h5 class="section-title">
                      <md-icon icon="gear-fill" aria-hidden="true" class="text-primary me-2">person</md-icon>Basic Information
                    </h5>
                    <b-table 
                      :items="personalInfoItems" 
                      :fields="personalInfoFields"
                      striped
                      hover
                      responsive
                      class="custom-table"
                    ></b-table>
                  </div>
                  <div class="col-md-6">
                    <h5 class="section-title">
                      <md-icon icon="gear-fill" aria-hidden="true" class="text-success me-2">credit_card</md-icon>Identity Documents
                    </h5>
                    <b-table 
                      :items="identityInfoItems" 
                      :fields="identityInfoFields"
                      striped
                      hover
                      responsive
                      class="custom-table"
                    ></b-table>
                  </div>
                </div>
              </b-tab>

              <!-- License Information Tab -->
              <b-tab title="License Details">
                <div class="row">
                  <div class="col-md-8">
                    <h5 class="section-title">
                      <md-icon icon="gear-fill" aria-hidden="true" class="text-warning me-2">credit_card</md-icon>License Information
                    </h5>
                    <b-table 
                      :items="licenseInfoItems" 
                      :fields="licenseInfoFields"
                      striped
                      hover
                      responsive
                      class="custom-table"
                    ></b-table>
                  </div>
                  <div class="col-md-4">
                    <h5 class="section-title">
                      <md-icon icon="gear-fill" aria-hidden="true" class="text-danger me-2">warning</md-icon>License Status
                    </h5>
                    <div class="license-status-card">
                      <div class="status-item">
                        <strong>Status:</strong>
                        <b-badge :variant="getLicenseStatusVariant()">
                          {{ getLicenseStatusText() }}
                        </b-badge>
                      </div>
                      <div class="status-item">
                        <strong>Days Remaining:</strong>
                        <span :class="getDaysRemainingClass()">
                          {{ getDaysRemaining() }} days
                        </span>
                      </div>
                      <div class="status-item">
                        <strong>Last Updated:</strong>
                        <span>{{ formatDate(getLicenseValidUntil()) }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- License Coverages -->
                <div class="row mt-4" v-if="driver.driver_license_coverages && driver.driver_license_coverages.length">
                  <div class="col-12">
                    <h5 class="section-title">
                      <md-icon icon="gear-fill" aria-hidden="true" class="text-info me-2">category</md-icon>License Coverages
                    </h5>
                    <b-table 
                      :items="licenseCoverageItems" 
                      :fields="licenseCoverageFields"
                      striped
                      hover
                      responsive
                      class="custom-table"
                    ></b-table>
                  </div>
                </div>

                <!-- License Image -->
                <div class="row mt-4" v-if="getLicenseImage()">
                  <div class="col-12">
                    <h5 class="section-title">
                      <md-icon icon="gear-fill" aria-hidden="true" class="text-warning me-2">image</md-icon>License Image
                    </h5>
                    <div class="text-center">
                      <img 
                        :src="getLicenseImage()" 
                        alt="Driver License"
                        class="license-image"
                        style="max-width: 400px; max-height: 300px; border: 1px solid #ddd; border-radius: 8px;"
                      >
                    </div>
                  </div>
                </div>

                <!-- License Transactions -->
                <div class="row mt-4" v-if="driver.driver_license_transactions && driver.driver_license_transactions.length">
                  <div class="col-12">
                    <h5 class="section-title">
                      <md-icon icon="gear-fill" aria-hidden="true" class="text-secondary me-2">history</md-icon>License Transactions
                    </h5>
                    <b-table 
                      :items="licenseTransactionItems" 
                      :fields="licenseTransactionFields"
                      striped
                      hover
                      responsive
                      class="custom-table"
                    ></b-table>
                  </div>
                </div>
              </b-tab>

              <!-- Employment Details Tab -->
              <b-tab title="Employment Details">
                <div class="row">
                  <div class="col-md-6">
                    <h5 class="section-title">
                      <md-icon icon="gear-fill" aria-hidden="true" class="text-info me-2">work</md-icon>Employment Information
                    </h5>
                    <b-table 
                      :items="employmentInfoItems" 
                      :fields="employmentInfoFields"
                      striped
                      hover
                      responsive
                      class="custom-table"
                    ></b-table>
                  </div>
                  <div class="col-md-6">
                    <h5 class="section-title">
                      <md-icon icon="gear-fill" aria-hidden="true" class="text-secondary me-2">local_shipping</md-icon>Vehicle Assignment
                    </h5>
                    <b-table 
                      :items="vehicleInfoItems" 
                      :fields="vehicleInfoFields"
                      striped
                      hover
                      responsive
                      class="custom-table"
                    ></b-table>
                  </div>
                </div>
              </b-tab>

              <!-- Bio Information Tab -->
              <b-tab title="Bio Information" v-if="driver.driver_bio_info && driver.driver_bio_info.length">
                <div class="row">
                  <div class="col-md-6">
                    <h5 class="section-title">
                      <md-icon icon="gear-fill" aria-hidden="true" class="text-primary me-2">person</md-icon>Personal Details
                    </h5>
                    <b-table 
                      :items="bioInfoItems" 
                      :fields="bioInfoFields"
                      striped
                      hover
                      responsive
                      class="custom-table"
                    ></b-table>
                  </div>
                  <div class="col-md-6">
                    <h5 class="section-title">
                      <md-icon icon="gear-fill" aria-hidden="true" class="text-success me-2">location_on</md-icon>Address Information
                    </h5>
                    <b-table 
                      :items="addressInfoItems" 
                      :fields="addressInfoFields"
                      striped
                      hover
                      responsive
                      class="custom-table"
                    ></b-table>
                  </div>
                </div>
              </b-tab>

              <!-- Documents Tab with Accordion -->
              <b-tab title="Documents">
                <div class="row">
                  <div class="col-12">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <h5 class="section-title mb-0">
                        <md-icon icon="gear-fill" aria-hidden="true" class="text-primary me-2">folder</md-icon>Uploaded Documents
                      </h5>
                      <b-button variant="primary" size="sm">
                        <md-icon icon="gear-fill" aria-hidden="true">upload</md-icon>Upload New Document
                      </b-button>
                    </div>
                    
                    <!-- Document Categories Accordion -->
                    <b-accordion>
                      <b-accordion-item title="License Documents" header-class="bg-primary text-white">
                        <b-row>
                          <b-col md="4" v-for="(doc, index) in getDocumentsByType('license')" :key="index">
                            <b-card class="document-card mb-3">
                              <div class="document-icon">
                                <md-icon icon="gear-fill" aria-hidden="true" class="text-danger">description</md-icon>
                              </div>
                              <div class="document-info">
                                <h6>{{ doc.name }}</h6>
                                <p class="text-muted mb-1">{{ doc.type }}</p>
                                <p class="text-muted mb-1 small">{{ doc.description }}</p>
                                <small class="text-muted">{{ formatDate(doc.uploadDate) }}</small>
                                <b-badge :variant="doc.status === 'Active' ? 'success' : doc.status === 'Completed' ? 'info' : 'warning'" class="mt-1">
                                  {{ doc.status }}
                                </b-badge>
                              </div>
                              <div class="document-actions mt-2">
                                <b-button size="sm" variant="outline-primary" class="me-1" @click="viewDocument(doc)">
                                  <md-icon icon="gear-fill" aria-hidden="true">visibility</md-icon>
                                </b-button>
                                <b-button size="sm" variant="outline-success" class="me-1" @click="downloadDocument(doc)">
                                  <md-icon icon="gear-fill" aria-hidden="true">download</md-icon>
                                </b-button>
                                <b-button size="sm" variant="outline-danger" @click="deleteDocument(doc)">
                                  <md-icon icon="gear-fill" aria-hidden="true">delete</md-icon>
                                </b-button>
                              </div>
                            </b-card>
                          </b-col>
                        </b-row>
                      </b-accordion-item>

                      <b-accordion-item title="Identity Documents" header-class="bg-success text-white">
                        <b-row>
                          <b-col md="4" v-for="(doc, index) in getDocumentsByType('identity')" :key="index">
                            <b-card class="document-card mb-3">
                              <div class="document-icon">
                                <md-icon icon="gear-fill" aria-hidden="true" class="text-info">description</md-icon>
                              </div>
                              <div class="document-info">
                                <h6>{{ doc.name }}</h6>
                                <p class="text-muted mb-1">{{ doc.type }}</p>
                                <p class="text-muted mb-1 small">{{ doc.description }}</p>
                                <small class="text-muted">{{ formatDate(doc.uploadDate) }}</small>
                                <b-badge :variant="doc.status === 'Active' ? 'success' : doc.status === 'Completed' ? 'info' : 'warning'" class="mt-1">
                                  {{ doc.status }}
                                </b-badge>
                              </div>
                              <div class="document-actions mt-2">
                                <b-button size="sm" variant="outline-primary" class="me-1" @click="viewDocument(doc)">
                                  <md-icon icon="gear-fill" aria-hidden="true">visibility</md-icon>
                                </b-button>
                                <b-button size="sm" variant="outline-success" class="me-1" @click="downloadDocument(doc)">
                                  <md-icon icon="gear-fill" aria-hidden="true">download</md-icon>
                                </b-button>
                                <b-button size="sm" variant="outline-danger" @click="deleteDocument(doc)">
                                  <md-icon icon="gear-fill" aria-hidden="true">delete</md-icon>
                                </b-button>
                              </div>
                            </b-card>
                          </b-col>
                        </b-row>
                      </b-accordion-item>

                      <b-accordion-item title="Medical Documents" header-class="bg-warning text-dark">
                        <b-row>
                          <b-col md="4" v-for="(doc, index) in getDocumentsByType('medical')" :key="index">
                            <b-card class="document-card mb-3">
                              <div class="document-icon">
                                <md-icon icon="gear-fill" aria-hidden="true" class="text-success">description</md-icon>
                              </div>
                              <div class="document-info">
                                <h6>{{ doc.name }}</h6>
                                <p class="text-muted mb-1">{{ doc.type }}</p>
                                <p class="text-muted mb-1 small">{{ doc.description }}</p>
                                <small class="text-muted">{{ formatDate(doc.uploadDate) }}</small>
                                <b-badge :variant="doc.status === 'Active' ? 'success' : doc.status === 'Completed' ? 'info' : 'warning'" class="mt-1">
                                  {{ doc.status }}
                                </b-badge>
                              </div>
                              <div class="document-actions mt-2">
                                <b-button size="sm" variant="outline-primary" class="me-1" @click="viewDocument(doc)">
                                  <md-icon icon="gear-fill" aria-hidden="true">visibility</md-icon>
                                </b-button>
                                <b-button size="sm" variant="outline-success" class="me-1" @click="downloadDocument(doc)">
                                  <md-icon icon="gear-fill" aria-hidden="true">download</md-icon>
                                </b-button>
                                <b-button size="sm" variant="outline-danger" @click="deleteDocument(doc)">
                                  <md-icon icon="gear-fill" aria-hidden="true">delete</md-icon>
                                </b-button>
                              </div>
                            </b-card>
                          </b-col>
                        </b-row>
                      </b-accordion-item>

                      <b-accordion-item title="Employment Documents" header-class="bg-info text-white">
                        <b-row>
                          <b-col md="4" v-for="(doc, index) in getDocumentsByType('employment')" :key="index">
                            <b-card class="document-card mb-3">
                              <div class="document-icon">
                                <md-icon icon="gear-fill" aria-hidden="true" class="text-primary">work</md-icon>
                              </div>
                              <div class="document-info">
                                <h6>{{ doc.name }}</h6>
                                <p class="text-muted mb-1">{{ doc.type }}</p>
                                <p class="text-muted mb-1 small">{{ doc.description }}</p>
                                <small class="text-muted">{{ formatDate(doc.uploadDate) }}</small>
                                <b-badge :variant="doc.status === 'Active' ? 'success' : doc.status === 'Completed' ? 'info' : 'warning'" class="mt-1">
                                  {{ doc.status }}
                                </b-badge>
                              </div>
                              <div class="document-actions mt-2">
                                <b-button size="sm" variant="outline-primary" class="me-1" @click="viewDocument(doc)">
                                  <md-icon icon="gear-fill" aria-hidden="true">visibility</md-icon>
                                </b-button>
                                <b-button size="sm" variant="outline-success" class="me-1" @click="downloadDocument(doc)">
                                  <md-icon icon="gear-fill" aria-hidden="true">download</md-icon>
                                </b-button>
                                <b-button size="sm" variant="outline-danger" @click="deleteDocument(doc)">
                                  <md-icon icon="gear-fill" aria-hidden="true">delete</md-icon>
                                </b-button>
                              </div>
                            </b-card>
                          </b-col>
                        </b-row>
                      </b-accordion-item>
                    </b-accordion>
                  </div>
                </div>
              </b-tab>



              <!-- Advanced Details Tab -->
              <b-tab title="Advanced Details">
                <div class="row">
                  <div class="col-md-6">
                    <h5 class="section-title">
                      <md-icon icon="gear-fill" aria-hidden="true" class="text-success me-2">trending_up</md-icon>Performance Metrics
                    </h5>
                    <b-card class="metrics-card">
                      <div class="metric-item">
                        <div class="metric-label">Safety Score</div>
                        <div class="metric-value">
                          <b-progress 
                            :value="85" 
                            variant="success" 
                            :max="100"
                            show-progress
                          ></b-progress>
                          <span class="metric-text">85%</span>
                        </div>
                      </div>
                      <div class="metric-item">
                        <div class="metric-label">On-Time Delivery</div>
                        <div class="metric-value">
                          <b-progress 
                            :value="92" 
                            variant="info" 
                            :max="100"
                            show-progress
                          ></b-progress>
                          <span class="metric-text">92%</span>
                        </div>
                      </div>
                      <div class="metric-item">
                        <div class="metric-label">Fuel Efficiency</div>
                        <div class="metric-value">
                          <b-progress 
                            :value="78" 
                            variant="warning" 
                            :max="100"
                            show-progress
                          ></b-progress>
                          <span class="metric-text">78%</span>
                        </div>
                      </div>
                    </b-card>
                  </div>
                  <div class="col-md-6">
                    <h5 class="section-title">
                      <md-icon icon="gear-fill" aria-hidden="true" class="text-secondary me-2">settings</md-icon>System Information
                    </h5>
                    <b-table 
                      :items="systemInfoItems" 
                      :fields="systemInfoFields"
                      striped
                      hover
                      responsive
                      class="custom-table"
                    ></b-table>
                  </div>
                </div>
              </b-tab>

              <!-- Raw Data Tab - Display Every Key -->
              <b-tab title="Raw Data">
                <div class="row">
                  <div class="col-12">
                    <h5 class="section-title">
                      <md-icon icon="gear-fill" aria-hidden="true" class="text-info me-2">code</md-icon>Complete API Response Data
                    </h5>
                    <p class="text-muted mb-4">Displaying every key from the API response for complete data visibility.</p>
                    
                    <!-- Main Driver Data -->
                    <b-card class="mb-4" header="Main Driver Information">
                      <b-table 
                        :items="rawDriverItems" 
                        :fields="rawDataFields"
                        striped
                        hover
                        responsive
                        class="custom-table"
                      ></b-table>
                    </b-card>

                    <!-- License Details -->
                    <b-card class="mb-4" header="License Details" v-if="driver.driver_license_details && driver.driver_license_details.length">
                      <b-table 
                        :items="rawLicenseDetailsItems" 
                        :fields="rawLicenseDetailsFields"
                        striped
                        hover
                        responsive
                        class="custom-table"
                      ></b-table>
                    </b-card>

                    <!-- License Object -->
                    <b-card class="mb-4" header="License Object" v-if="driver.driver_license_obj && driver.driver_license_obj.length">
                      <b-table 
                        :items="rawLicenseObjItems" 
                        :fields="rawLicenseObjFields"
                        striped
                        hover
                        responsive
                        class="custom-table"
                      ></b-table>
                    </b-card>

                    <!-- License Coverages -->
                    <b-card class="mb-4" header="License Coverages" v-if="driver.driver_license_coverages && driver.driver_license_coverages.length">
                      <b-table 
                        :items="rawLicenseCoveragesItems" 
                        :fields="rawLicenseCoveragesFields"
                        striped
                        hover
                        responsive
                        class="custom-table"
                      ></b-table>
                    </b-card>

                    <!-- License Transactions -->
                    <b-card class="mb-4" header="License Transactions" v-if="driver.driver_license_transactions && driver.driver_license_transactions.length">
                      <b-table 
                        :items="rawLicenseTransactionsItems" 
                        :fields="rawLicenseTransactionsFields"
                        striped
                        hover
                        responsive
                        class="custom-table"
                      ></b-table>
                    </b-card>

                    <!-- Bio Information -->
                    <b-card class="mb-4" header="Bio Information" v-if="driver.driver_bio_info && driver.driver_bio_info.length">
                      <b-table 
                        :items="rawBioInfoItems" 
                        :fields="rawBioInfoFields"
                        striped
                        hover
                        responsive
                        class="custom-table"
                      ></b-table>
                    </b-card>

                    <!-- Bio Image Object -->
                    <b-card class="mb-4" header="Bio Image Object" v-if="driver.driver_bio_img_obj && driver.driver_bio_img_obj.length">
                      <b-table 
                        :items="rawBioImgItems" 
                        :fields="rawBioImgFields"
                        striped
                        hover
                        responsive
                        class="custom-table"
                      ></b-table>
                    </b-card>
                  </div>
                </div>
              </b-tab>
            </b-tabs>
          </b-card-text>
        </b-card>
      </b-card-text>
    </b-card>

    <!-- Document Viewer Modal -->
    <b-modal 
      v-model="showDocumentModal" 
      :title="selectedDocument ? selectedDocument.name : 'Document Viewer'"
      size="lg"
      scrollable
      centered
    >
      <div v-if="selectedDocument" class="document-viewer">
        <div class="document-header mb-3">
          <div class="row">
            <div class="col-md-6">
              <h6 class="text-primary">{{ selectedDocument.name }}</h6>
              <p class="text-muted mb-1">{{ selectedDocument.type }}</p>
              <p class="text-muted small">{{ selectedDocument.description }}</p>
            </div>
            <div class="col-md-6 text-right">
              <b-badge :variant="selectedDocument.status === 'Active' ? 'success' : selectedDocument.status === 'Completed' ? 'info' : 'warning'" class="mb-2">
                {{ selectedDocument.status }}
              </b-badge>
              <br>
              <small class="text-muted">Uploaded: {{ formatDate(selectedDocument.uploadDate) }}</small>
            </div>
          </div>
        </div>
        
        <div class="document-content">
          <div class="text-center py-4">
            <md-icon icon="gear-fill" aria-hidden="true" class="text-muted" style="font-size: 4rem;">description</md-icon>
            <p class="text-muted mt-2">Document preview not available</p>
            <p class="text-muted small">This is a placeholder for the actual document content.</p>
          </div>
        </div>
      </div>
      
      <template #modal-footer>
        <div class="w-100 d-flex justify-content-between">
          <b-button variant="secondary" @click="showDocumentModal = false">
            Close
          </b-button>
          <div>
            <b-button variant="primary" @click="downloadDocument(selectedDocument)" class="me-2">
              <md-icon icon="gear-fill" aria-hidden="true">download</md-icon>
              Download
            </b-button>
            <b-button variant="danger" @click="deleteDocument(selectedDocument)">
              <md-icon icon="gear-fill" aria-hidden="true">delete</md-icon>
              Delete
            </b-button>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import CustomLoader from "../../common/customLoader.vue";
import DriverService from "../../../services/DriverService.js";

export default {
  name: 'DriverDetailView',
  components: {
    CustomLoader
  },
  data() {
    return {
      isLoading: false,
      errorMessage: '',
      driver: null,
      driverDocuments: [],
      showDocumentModal: false,
      selectedDocument: null,
      // Table fields and items
      personalInfoFields: [
        { key: 'field', label: 'Field' },
        { key: 'value', label: 'Value' }
      ],
      personalInfoItems: [],
      identityInfoFields: [
        { key: 'field', label: 'Document Type' },
        { key: 'value', label: 'Number' }
      ],
      identityInfoItems: [],
      licenseInfoFields: [
        { key: 'field', label: 'License Field' },
        { key: 'value', label: 'Details' }
      ],
      licenseInfoItems: [],
      licenseCoverageFields: [
        { key: 'coverage', label: 'Coverage Type' },
        { key: 'description', label: 'Description' },
        { key: 'issuedDate', label: 'Issued Date' },
        { key: 'status', label: 'Status' }
      ],
      licenseCoverageItems: [],
      licenseTransactionFields: [
        { key: 'transaction', label: 'Transaction Type' },
        { key: 'date', label: 'Date' },
        { key: 'office', label: 'RTO Office' },
        { key: 'description', label: 'Description' }
      ],
      licenseTransactionItems: [],
      employmentInfoFields: [
        { key: 'field', label: 'Employment Field' },
        { key: 'value', label: 'Details' }
      ],
      employmentInfoItems: [],
      vehicleInfoFields: [
        { key: 'field', label: 'Vehicle Field' },
        { key: 'value', label: 'Details' }
      ],
      vehicleInfoItems: [],
      bioInfoFields: [
        { key: 'field', label: 'Bio Field' },
        { key: 'value', label: 'Details' }
      ],
      bioInfoItems: [],
      addressInfoFields: [
        { key: 'field', label: 'Address Field' },
        { key: 'value', label: 'Details' }
      ],
      addressInfoItems: [],
      systemInfoFields: [
        { key: 'field', label: 'System Field' },
        { key: 'value', label: 'Details' }
      ],
      systemInfoItems: [],
      // Raw Data Fields and Items
      rawDataFields: [
        { key: 'field', label: 'Field' },
        { key: 'value', label: 'Value' }
      ],
      rawDriverItems: [],
      rawLicenseDetailsFields: [
        { key: 'field', label: 'Field' },
        { key: 'value', label: 'Value' }
      ],
      rawLicenseDetailsItems: [],
      rawLicenseObjFields: [
        { key: 'field', label: 'Field' },
        { key: 'value', label: 'Value' }
      ],
      rawLicenseObjItems: [],
      rawLicenseCoveragesFields: [
        { key: 'field', label: 'Field' },
        { key: 'value', label: 'Value' }
      ],
      rawLicenseCoveragesItems: [],
      rawLicenseTransactionsFields: [
        { key: 'field', label: 'Field' },
        { key: 'value', label: 'Value' }
      ],
      rawLicenseTransactionsItems: [],
      rawBioInfoFields: [
        { key: 'field', label: 'Field' },
        { key: 'value', label: 'Value' }
      ],
      rawBioInfoItems: [],
      rawBioImgFields: [
        { key: 'field', label: 'Field' },
        { key: 'value', label: 'Value' }
      ],
      rawBioImgItems: []
    }
  },
  mounted() {
    this.loadDriverData();
  },
  methods: {
    async loadDriverData() {
      this.isLoading = true;
      this.errorMessage = '';
      
      try {
        // Get driver ID from route params
        const driverId = this.$route.params.driverId;
        
        if (!driverId) {
          this.errorMessage = 'Driver ID is required';
          return;
        }

        const response = await DriverService.getDriverInfo({driverId:driverId});
        
        if (response.data && response.data.success) {
          this.driver = response.data.data;
          this.populateTableData();
          this.populateDocuments(); // Populate documents from API data
          this.populateRawData(); // Populate raw data after main data is loaded
        } else {
          this.errorMessage = response.data.message || 'Failed to load driver information';
        }
      } catch (error) {
        console.error('Error loading driver data:', error);
        
        if (error.response) {
          // Server responded with error status
          if (error.response.status === 404) {
            this.errorMessage = 'Driver not found. Please check the driver ID.';
          } else if (error.response.status === 401) {
            this.errorMessage = 'Unauthorized. Please login again.';
          } else if (error.response.status >= 500) {
            this.errorMessage = 'Server error. Please try again later.';
          } else {
            this.errorMessage = `Error: ${error.response.data.message || 'Failed to load driver information'}`;
          }
        } else if (error.request) {
          // Network error
          this.errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          // Other error
          this.errorMessage = 'Failed to load driver information. Please try again.';
        }
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleDateString('en-IN');
      } catch (error) {
        return 'Invalid Date';
      }
    },
    getStatusClass(status) {
      return status === 1 ? 'status-active' : 'status-inactive';
    },
    getStatusText(status) {
      return status === 1 ? 'Active' : 'Inactive';
    },
    getExperienceYears() {
      if (!this.driver) return '0';
      
      // Calculate experience from joining date
      if (this.driver.driverDateOfJoining) {
        const joiningDate = new Date(this.driver.driverDateOfJoining);
        const currentDate = new Date();
        const diffTime = currentDate - joiningDate;
        const diffYears = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365.25));
        return diffYears.toString();
      }
      
      return this.driver.driverWorkingYear || '0';
    },
    getLicenseClass() {
      if (!this.driver || !this.driver.driver_license_coverages) return 'N/A';
      
      const coverages = this.driver.driver_license_coverages;
      const coverageTypes = coverages.map(cov => cov.covabbrv).filter(Boolean);
      return coverageTypes.length > 0 ? coverageTypes.join(', ') : 'N/A';
    },
    getLicenseValidUntil() {
      if (!this.driver || !this.driver.driver_license_obj || !this.driver.driver_license_obj.length) {
        return null;
      }
      
      const licenseObj = this.driver.driver_license_obj[0];
      return licenseObj.dlTrValdtoDt || licenseObj.dlNtValdtoDt;
    },
    getLicenseStatusVariant() {
      const daysRemaining = this.getDaysRemaining();
      if (daysRemaining < 0) return 'danger';
      if (daysRemaining < 30) return 'warning';
      if (daysRemaining < 90) return 'info';
      return 'success';
    },
    getLicenseStatusText() {
      const daysRemaining = this.getDaysRemaining();
      if (daysRemaining < 0) return 'Expired';
      if (daysRemaining < 30) return 'Expiring Soon';
      if (daysRemaining < 90) return 'Valid';
      return 'Valid';
    },
    getDaysRemaining() {
      const validUntil = this.getLicenseValidUntil();
      if (!validUntil) return 0;
      
      const today = new Date();
      const validUntilDate = new Date(validUntil);
      const diffTime = validUntilDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    },
    getDaysRemainingClass() {
      const days = this.getDaysRemaining();
      if (days < 0) return 'text-danger';
      if (days < 30) return 'text-warning';
      if (days < 90) return 'text-info';
      return 'text-success';
    },

    populateTableData() {
      if (!this.driver) return;

      // Personal Information
      this.personalInfoItems = [
        { field: 'Full Name', value: this.driver.driverName || 'N/A' },
        { field: 'First Name', value: this.driver.driver_fname || 'N/A' },
        { field: 'Middle Name', value: this.driver.driver_mname || 'N/A' },
        { field: 'Last Name', value: this.driver.driver_lname || 'N/A' },
        { field: 'Date of Birth', value: this.formatDate(this.driver.driverDateOfBirth) },
        { field: 'Contact Number', value: this.driver.driverContact || 'N/A' },
        { field: 'Location', value: this.driver.driverLocation || 'N/A' },
        { field: 'Driver Type', value: this.driver.driverType === '1' ? 'Permanent' : 'Temporary' }
      ];

      // Identity Information
      this.identityInfoItems = [
        { field: 'Aadhar Number', value: this.driver.driverAadharNumber || 'N/A' },
        { field: 'UAN Number', value: this.driver.driverUANNumber || 'N/A' },
        { field: 'ESI Number', value: this.driver.driverEsic || 'N/A' },
        { field: 'Account Number', value: this.driver.accountNo || 'N/A' }
      ];

      // License Information
      this.licenseInfoItems = [
        { field: 'License Number', value: this.driver.license_no || 'N/A' },
        { field: 'Original License', value: this.driver.org_license_no || 'N/A' },
        { field: 'License Class', value: this.getLicenseClass() },
        { field: 'Valid Until', value: this.formatDate(this.getLicenseValidUntil()) },
        { field: 'Status', value: this.getLicenseStatusText() },
        { field: 'Days Remaining', value: `${this.getDaysRemaining()} days` }
      ];

      // License Coverages
      if (this.driver.driver_license_coverages) {
        this.licenseCoverageItems = this.driver.driver_license_coverages.map(coverage => ({
          coverage: coverage.covabbrv || 'N/A',
          description: coverage.covdesc || 'N/A',
          issuedDate: this.formatDate(coverage.dcIssuedt),
          status: coverage.dcCovStatus === 'A' ? 'Active' : 'Inactive'
        }));
      }

      // License Transactions
      if (this.driver.driver_license_transactions) {
        this.licenseTransactionItems = this.driver.driver_license_transactions.map(transaction => ({
          transaction: transaction.trName || 'N/A',
          date: this.formatDate(transaction.trEntrydt),
          office: transaction.olaOffName || 'N/A',
          description: transaction.trAbbr || 'N/A'
        }));
      }

      // Employment Information
      this.employmentInfoItems = [
        { field: 'Employee Code', value: this.driver.driverECode || 'N/A' },
        { field: 'Date of Joining', value: this.formatDate(this.driver.driverDateOfJoining) },
        { field: 'Date of Leaving', value: this.formatDate(this.driver.driverDateOfLeaving) || 'Active' },
        { field: 'Working Years', value: `${this.getExperienceYears()} years` },
        { field: 'Experience', value: `${this.getExperienceYears()} years` }
      ];

      // Vehicle Information
      this.vehicleInfoItems = [
        { field: 'Assigned Vehicle', value: this.driver.driverVehicleNumber || 'Not Assigned' },
        { field: 'Vehicle Type', value: 'Truck' }, // Default value since not in API
        { field: 'Vehicle ID', value: this.driver.vehicleTruckId || 'N/A' },
        { field: 'Registration', value: this.driver.driverVehicleNumber || 'N/A' }
      ];

      // Bio Information
      if (this.driver.driver_bio_info && this.driver.driver_bio_info.length > 0) {
        const bioInfo = this.driver.driver_bio_info[0];
        this.bioInfoItems = [
          { field: 'Full Name', value: bioInfo.bioFullName || 'N/A' },
          { field: 'First Name', value: bioInfo.bioFirstName || 'N/A' },
          { field: 'Middle Name', value: bioInfo.bioMiddleName || 'N/A' },
          { field: 'Last Name', value: bioInfo.bioLastName || 'N/A' },
          { field: 'Date of Birth', value: bioInfo.bioDob ? this.formatDate(bioInfo.bioDob) : 'N/A' },
          { field: 'Gender', value: bioInfo.bioGenderDesc || 'N/A' },
          { field: 'Blood Group', value: bioInfo.bioBloodGroupname || 'N/A' },
          { field: 'Mobile Number', value: bioInfo.bioMobileNo || 'N/A' },
          { field: 'Email', value: bioInfo.bioEmailId || 'N/A' },
          { field: 'Nationality', value: bioInfo.bioCitiZen || 'N/A' },
          { field: 'Organ Donor', value: bioInfo.bioOrganDonor === 'Y' ? 'Yes' : 'No' },
          { field: 'Qualification', value: bioInfo.bioQmQualdesc || 'N/A' }
        ];

        // Address Information from bio data
        this.addressInfoItems = [
          { field: 'Permanent Address 1', value: bioInfo.bioPermAdd1 || 'N/A' },
          { field: 'Permanent Address 2', value: bioInfo.bioPermAdd2 || 'N/A' },
          { field: 'Permanent Address 3', value: bioInfo.bioPermAdd3 || 'N/A' },
          { field: 'Permanent District', value: bioInfo.bioPermDistName || 'N/A' },
          { field: 'Permanent State', value: bioInfo.bioPermSdName || 'N/A' },
          { field: 'Permanent PIN', value: bioInfo.bioPermPin || 'N/A' },
          { field: 'Temporary Address 1', value: bioInfo.bioTempAdd1 || 'N/A' },
          { field: 'Temporary Address 2', value: bioInfo.bioTempAdd2 || 'N/A' },
          { field: 'Temporary Address 3', value: bioInfo.bioTempAdd3 || 'N/A' },
          { field: 'Temporary District', value: bioInfo.bioTempDistCd || 'N/A' },
          { field: 'Temporary State', value: bioInfo.bioTempSdName || 'N/A' },
          { field: 'Temporary PIN', value: bioInfo.bioTempPin || 'N/A' }
        ];
      }

      // System Information
      this.systemInfoItems = [
        { field: 'Last Login', value: this.formatDate(this.driver.loginTime) },
        { field: 'Created By', value: 'System' }, // Default since not in API
        { field: 'Updated By', value: 'System' }, // Default since not in API
        { field: 'Created On', value: this.formatDate(this.driver.driverDateOfJoining) },
        { field: 'Last Updated', value: this.formatDate(this.driver.loginTime) }
      ];
    },
    
    populateDocuments() {
      if (!this.driver) return;
      
      this.driverDocuments = [];
      
      // Add Driver License Document
      if (this.driver.license_no) {
        this.driverDocuments.push({
          name: 'Driver License',
          type: 'License Document',
          category: 'license',
          uploadDate: this.driver.driver_license_obj && this.driver.driver_license_obj[0] ? 
            this.driver.driver_license_obj[0].dlIssuedt : this.driver.driverDateOfJoining,
          url: '#',
          description: `License No: ${this.driver.license_no}`,
          status: 'Active'
        });
      }
      
      // Add Aadhar Card Document
      if (this.driver.driverAadharNumber) {
        this.driverDocuments.push({
          name: 'Aadhar Card',
          type: 'Identity Document',
          category: 'identity',
          uploadDate: this.driver.driverDateOfJoining,
          url: '#',
          description: `Aadhar No: ${this.driver.driverAadharNumber}`,
          status: 'Active'
        });
      }
      
      // Add License Coverages as separate documents
      if (this.driver.driver_license_coverages && this.driver.driver_license_coverages.length > 0) {
        this.driver.driver_license_coverages.forEach((coverage, index) => {
          this.driverDocuments.push({
            name: `${coverage.covabbrv} License Coverage`,
            type: 'License Coverage Document',
            category: 'license',
            uploadDate: coverage.dcIssuedt,
            url: '#',
            description: coverage.covdesc || 'License Coverage',
            status: coverage.dcCovStatus === 'A' ? 'Active' : 'Inactive'
          });
        });
      }
      
      // Add License Transactions as documents
      if (this.driver.driver_license_transactions && this.driver.driver_license_transactions.length > 0) {
        this.driver.driver_license_transactions.forEach((transaction, index) => {
          this.driverDocuments.push({
            name: transaction.trName || 'License Transaction',
            type: 'License Transaction Document',
            category: 'license',
            uploadDate: transaction.trEntrydt,
            url: '#',
            description: transaction.trAbbr || 'License Transaction',
            status: 'Completed'
          });
        });
      }
      
      // Add Bio Information as identity document
      if (this.driver.driver_bio_info && this.driver.driver_bio_info.length > 0) {
        const bioInfo = this.driver.driver_bio_info[0];
        this.driverDocuments.push({
          name: 'Bio Information',
          type: 'Identity Document',
          category: 'identity',
          uploadDate: bioInfo.bioEndorsedt,
          url: '#',
          description: `Bio ID: ${bioInfo.bioBioId}`,
          status: 'Active'
        });
      }
      
      // Add Photo Document if available
      if (this.driver.driver_bio_img_obj && this.driver.driver_bio_img_obj.length > 0) {
        const bioImg = this.driver.driver_bio_img_obj[0];
        if (bioImg.biPhoto) {
          this.driverDocuments.push({
            name: 'Driver Photo',
            type: 'Photo Document',
            category: 'identity',
            uploadDate: bioImg.biPhotoDate,
            url: '#',
            description: 'Driver Photograph',
            status: 'Active'
          });
        }
      }
      
      // Add ESI Document
      if (this.driver.driverEsic) {
        this.driverDocuments.push({
          name: 'ESI Certificate',
          type: 'Employment Document',
          category: 'employment',
          uploadDate: this.driver.driverDateOfJoining,
          url: '#',
          description: `ESI No: ${this.driver.driverEsic}`,
          status: 'Active'
        });
      }
      
      // Add UAN Document
      if (this.driver.driverUANNumber) {
        this.driverDocuments.push({
          name: 'UAN Certificate',
          type: 'Employment Document',
          category: 'employment',
          uploadDate: this.driver.driverDateOfJoining,
          url: '#',
          description: `UAN No: ${this.driver.driverUANNumber}`,
          status: 'Active'
        });
      }
    },
         populateRawData() {
       if (!this.driver) return;

       // Main Driver Information - Filter out arrays and objects to show only direct properties
       const mainDriverKeys = Object.keys(this.driver).filter(key => 
         !Array.isArray(this.driver[key]) && 
         typeof this.driver[key] !== 'object' || 
         this.driver[key] === null
       );
       
       this.rawDriverItems = mainDriverKeys.map(key => ({
         field: key,
         value: this.driver[key] !== null ? this.driver[key] : 'N/A'
       }));

       // License Details
       if (this.driver.driver_license_details && this.driver.driver_license_details.length) {
         this.rawLicenseDetailsItems = this.driver.driver_license_details.map((detail, index) => 
           Object.entries(detail).map(([key, value]) => ({
             field: `${index + 1}. ${key}`,
             value: value !== null ? value : 'N/A'
           }))
         ).flat();
       }

       // License Object
       if (this.driver.driver_license_obj && this.driver.driver_license_obj.length) {
         this.rawLicenseObjItems = Object.entries(this.driver.driver_license_obj[0]).map(([key, value]) => ({
           field: key,
           value: value !== null ? value : 'N/A'
         }));
       }

       // License Coverages
       if (this.driver.driver_license_coverages && this.driver.driver_license_coverages.length) {
         this.rawLicenseCoveragesItems = this.driver.driver_license_coverages.map((coverage, index) => 
           Object.entries(coverage).map(([key, value]) => ({
             field: `${index + 1}. ${key}`,
             value: value !== null ? value : 'N/A'
           }))
         ).flat();
       }

       // License Transactions
       if (this.driver.driver_license_transactions && this.driver.driver_license_transactions.length) {
         this.rawLicenseTransactionsItems = this.driver.driver_license_transactions.map((transaction, index) => 
           Object.entries(transaction).map(([key, value]) => ({
             field: `${index + 1}. ${key}`,
             value: value !== null ? value : 'N/A'
           }))
         ).flat();
       }

       // Bio Information
       if (this.driver.driver_bio_info && this.driver.driver_bio_info.length) {
         this.rawBioInfoItems = Object.entries(this.driver.driver_bio_info[0]).map(([key, value]) => ({
           field: key,
           value: value !== null ? value : 'N/A'
         }));
       }

       // Bio Image Object
       if (this.driver.driver_bio_img_obj && this.driver.driver_bio_img_obj.length) {
         this.rawBioImgItems = Object.entries(this.driver.driver_bio_img_obj[0]).map(([key, value]) => ({
           field: key,
           value: value !== null ? value : 'N/A'
         }));
       }
     },
    goBack() {
      this.$router.go(-1);
    },
    editDriver() {
      console.log('Edit driver:', this.driver);
    },
    printDriverInfo() {
      window.print();
    },
    getDocumentsByType(category) {
      return this.driverDocuments.filter(doc => doc.category === category);
    },
    getLicenseImage() {
      if (!this.driver || !this.driver.driver_bio_img_obj || !this.driver.driver_bio_img_obj.length) {
        return null;
      }
      
      const bioImg = this.driver.driver_bio_img_obj[0];
      if (bioImg.biPhoto) {
        // Convert base64 to data URL if needed
        return bioImg.biPhoto.startsWith('data:') ? bioImg.biPhoto : `data:image/jpeg;base64,${bioImg.biPhoto}`;
      }
      return null;
    },
    viewDocument(doc) {
      try {
        this.selectedDocument = doc;
        this.showDocumentModal = true;
        
        // If the document has a URL, you could open it in a new window
        if (doc.url && doc.url !== '#') {
          window.open(doc.url, '_blank');
        }
      } catch (error) {
        console.error('Error viewing document:', error);
        this.$bvToast.toast('Error viewing document', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },
    downloadDocument(doc) {
      try {
        // Create a blob URL for the document
        let blob;
        let filename = `${doc.name.replace(/\s+/g, '_')}.pdf`;
        
        // For demonstration, we'll create a simple PDF-like content
        // In a real implementation, you would get the actual document data from the API
        const content = `
Document: ${doc.name}
Type: ${doc.type}
Description: ${doc.description}
Status: ${doc.status}
Upload Date: ${this.formatDate(doc.uploadDate)}
Driver ID: ${this.driver.driverId}
Driver Name: ${this.driver.driverName}
        `;
        
        blob = new Blob([content], { type: 'text/plain' });
        filename = `${doc.name.replace(/\s+/g, '_')}.txt`;
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.$bvToast.toast('Document downloaded successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        console.error('Error downloading document:', error);
        this.$bvToast.toast('Error downloading document', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },
    deleteDocument(doc) {
      try {
        this.$bvModal.msgBoxConfirm(
          `Are you sure you want to delete "${doc.name}"? This action cannot be undone.`,
          {
            title: 'Confirm Delete',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Delete',
            cancelTitle: 'Cancel',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true
          }
        ).then(value => {
          if (value) {
            // Remove document from the array
            const index = this.driverDocuments.findIndex(d => d.name === doc.name && d.type === doc.type);
            if (index > -1) {
              this.driverDocuments.splice(index, 1);
              
              this.$bvToast.toast('Document deleted successfully', {
                title: 'Success',
                variant: 'success',
                solid: true
              });
            }
            
            // In a real implementation, you would make an API call to delete the document
            console.log('Document deleted:', doc);
          }
        });
      } catch (error) {
        console.error('Error deleting document:', error);
        this.$bvToast.toast('Error deleting document', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    }
  }
}
</script>

<style scoped>
.driver-avatar-container {
  position: relative;
  display: inline-block;
}

.driver-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 4px solid #007bff;
  object-fit: cover;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.status-badge {
  position: absolute;
  bottom: 5px;
  right: 5px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.status-badge.status-active {
  background: #28a745;
  color: white;
}

.status-badge.status-inactive {
  background: #dc3545;
  color: white;
}

.driver-name {
  color: #2c3e50;
  font-weight: 700;
  margin-bottom: 5px;
}

.driver-id {
  font-size: 0.9rem;
  margin-bottom: 10px;
}

.driver-badges {
  margin-bottom: 15px;
}

.quick-stats {
  text-align: right;
}

.stat-item {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 8px;
  font-size: 0.9rem;
}

.stat-item i {
  margin-right: 8px;
  width: 16px;
}

.section-title {
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e9ecef;
}

.custom-table {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.custom-table .table {
  margin-bottom: 0;
}

.license-status-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border-left: 4px solid #007bff;
}

.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e9ecef;
}

.status-item:last-child {
  border-bottom: none;
}

.document-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.document-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.document-icon {
  text-align: center;
  font-size: 2rem;
  margin-bottom: 10px;
}

.document-info h6 {
  margin-bottom: 5px;
  color: #2c3e50;
  font-weight: 600;
}

.document-info p {
  margin-bottom: 5px;
  font-size: 0.9rem;
}

.document-info small {
  color: #adb5bd;
}

.document-actions {
  display: flex;
  justify-content: center;
  gap: 5px;
}

.activity-content {
  padding: 15px 0;
}

.activity-meta {
  display: flex;
  gap: 20px;
  margin-bottom: 10px;
  font-size: 0.9rem;
  color: #6c757d;
}

.activity-description {
  margin-bottom: 15px;
  color: #2c3e50;
}

.activity-actions {
  display: flex;
  gap: 10px;
}

.metrics-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
}

.metric-item {
  margin-bottom: 20px;
}

.metric-label {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
}

.metric-value {
  position: relative;
}

.metric-text {
  position: absolute;
  right: 0;
  top: 0;
  font-weight: 600;
  color: #2c3e50;
}

.license-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.document-viewer {
  padding: 20px;
}

.document-header {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 15px;
}

.document-content {
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border-radius: 8px;
  margin: 20px 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .driver-avatar {
    width: 100px;
    height: 100px;
  }
  
  .quick-stats {
    text-align: left;
    margin-top: 15px;
  }
  
  .stat-item {
    justify-content: flex-start;
  }
  
  .activity-meta {
    flex-direction: column;
    gap: 5px;
  }
  
  .activity-actions {
    flex-direction: column;
  }
}

@media (max-width: 576px) {
  .driver-name {
    font-size: 1.5rem;
  }
  
  .driver-badges {
    text-align: center;
  }
  
  .document-actions {
    flex-direction: column;
  }
}

/* Print Styles */
@media print {
  .formCard {
    box-shadow: none !important;
  }
  
  .btn {
    display: none !important;
  }
  
  .tabs {
    display: block !important;
  }
  
  .tab-content {
    display: block !important;
  }
}
</style> 


// WEBPACK FOOTER //
// DriverDetailView.vue?0f15fee9